import { DateTime } from 'luxon';
import * as Yup from 'yup';
import {
  numberFieldValidator,
  optionalNumberFieldValidator,
  requiredNumberFieldValidator
} from '@portal_components/validation/ValidationSchemas';
import { invoiceValidationSchema } from '../elements/group/GroupView';
import {
  Contract,
  ENewOrUpdatePrices,
  EPageViewType,
  Service,
  EPriceType,
  ServicePrice,
  MeterPurchasePrice,
  Feature,
  Mpid,
  Premium
} from '../types';
import { useContractStandingData } from './useContractStandingData';

export const useContractValidationSchema = (viewType: EPageViewType, isSupplier: boolean) => {
  const {
    standingData: { standingDataServiceCodeList, standingDataStandardDaysList }
  } = useContractStandingData();

  const servicePriceSchema = Yup.object({
    chargingPrice: optionalNumberFieldValidator,
    annualBasePrice: Yup.lazy(value => {
      return numberFieldValidator.when(['chargingPrice'], ([chargingPrice]) => {
        if (chargingPrice) {
          return requiredNumberFieldValidator;
        }
        if (value === '') return Yup.string().optional();
        return numberFieldValidator.optional();
      });
    })
  });

  const commsServicePriceSchema = Yup.object({
    inherited: servicePriceSchema,
    nonInherited: servicePriceSchema
  });

  return Yup.object<Contract, { [Key in keyof Contract]: Yup.AnySchema<Contract[Key]> }>({
    updateOrNewPrices: Yup.string<ENewOrUpdatePrices>().optional(),
    contractPriceStartDate: Yup.string().when(['updateOrNewPrices'], ([updateOrNewPrices], schema) => {
      if (updateOrNewPrices === ENewOrUpdatePrices.NEW) {
        return schema.required('Field is required');
      }
      return schema.optional();
    }),
    // contractPriceEndDate: Yup.string().when(['updateOrNewPrices'], ([updateOrNewPrices], schema) => {
    //   if (updateOrNewPrices === ENewOrUpdatePrices.NEW) {
    //     return schema.required('Field is required');
    //   }
    //   return schema.optional();
    // }),
    accountManager: Yup.string().required('Field is required'),
    contractStartDate: Yup.string().required('Field is required'),
    contractEndDate: Yup.string()
      .required('Field is required')
      .test('end-date-not-in-past', 'Contract end date cannot be in the past', value => {
        if (viewType === EPageViewType.EDIT) return true;
        if (!value) return false;
        const currentDate = DateTime.now().startOf('day');
        const endDate = DateTime.fromISO(value).startOf('day');
        return endDate >= currentDate;
      }),
    letterOfAuthority: Yup.string<'Y' | 'N'>().required('Field is required'),
    evergreen: Yup.string<'Y' | 'N'>().required('Field is required'),
    assetOwnership: Yup.string().required('Field is required'),
    contractType: Yup.string().required('Field is required'),
    businessAreaId: Yup.string().required('Field is required'),
    energyManagementType: Yup.string().when(['serviceList'], ([serviceList], schema) => {
      if (!standingDataServiceCodeList) return schema;
      const isDCE = (serviceList as Service[])?.find(service => {
        const serviceCode = standingDataServiceCodeList.find(s => {
          return s.value == service.srvCodeId;
        });
        if (!serviceCode) return false;
        return serviceCode.title.includes('DCE');
      });
      return schema.test('is-dce', 'Field is required only for DCE services', value => {
        if (isDCE) {
          return !!value;
        } else {
          return !value;
        }
      });
    }),
    upliftDate: Yup.string().when(['upliftTypeId'], ([upliftTypeId], schema) => {
      return upliftTypeId ? schema.required('Field is required if Uplift Date selected') : schema.optional();
    }),
    billPeriod: Yup.string().required('Field is required'),
    billDay: requiredNumberFieldValidator
      .moreThan(0, 'Must be a number between 1 and 27')
      .lessThan(28, 'Must be a number between 1 and 27'),
    billingType: viewType === EPageViewType.ADD ? Yup.string().required('Field is required') : Yup.string(),
    standardDaysId: Yup.string()
      .oneOf(standingDataStandardDaysList.map(item => item.value))
      .required('Field is required'),
    oohPrices: Yup.array(
      Yup.object({
        dayOfWeek: Yup.number().required('Field is required'),
        fromTime: Yup.string()
          .required('Field is required')
          .matches(/^[0-2][0-9][0-5][0-9]0{2}$/, 'Invalid time format'),
        toTime: Yup.string()
          .required('Field is required')
          .matches(/^[0-2][0-9][0-5][0-9]0{2}$/, 'Invalid time format'),
        hourlyBasePrice: requiredNumberFieldValidator
      })
    ),
    serviceList: Yup.array<Service>(
      Yup.object({
        srvCodeId: Yup.string().required('Field is required'),
        srvStartDate: Yup.string()
          .required('Field is required')
          .test(
            'start-after-contract-start',
            'Start date must be on or after contract start date',
            (value, context) => {
              // this is a workaround due to not being about to access higher level fields with Schema.when()
              const contractStartDate = (context.options as any).from[1].value.contractStartDate;
              if (!contractStartDate || !value) return false;
              const srvStart = DateTime.fromISO(value);
              const contractStart = DateTime.fromISO(contractStartDate);
              return srvStart >= contractStart;
            }
          )
          .test(
            'start-before-contract-end',
            'Start date must be before contract end date',
            (value, context) => {
              const contractEndDate = (context.options as any).from[1].value.contractEndDate;
              if (!value || !contractEndDate) return false;
              const srvStart = DateTime.fromISO(value);
              const contractEnd = DateTime.fromISO(contractEndDate);
              return srvStart < contractEnd;
            }
          ),
        srvEndDate: Yup.string()
          .optional()
          .when(['srvStartDate'], ([srvStartDate], schema) => {
            return schema.test(
              'end-after-service-start',
              'End date must be after service start date',
              value => {
                if (!value) return true;
                if (!srvStartDate) return false;
                const srvEnd = DateTime.fromISO(value);
                const srvStart = DateTime.fromISO(srvStartDate);
                return srvEnd > srvStart;
              }
            );
          })
          .test(
            'end-before-contract-end',
            'End date must be before or on contract end date',
            (value, context) => {
              if (!value) return true;
              const contractEndDate = (context.options as any).from[1].value.contractEndDate;
              if (!contractEndDate) return false;
              const srvStart = DateTime.fromISO(value);
              const contractEnd = DateTime.fromISO(contractEndDate);
              return srvStart <= contractEnd;
            }
          ),
        siteDetails: Yup.array(
          Yup.object({
            siteTypeId: Yup.string().required('Field is required'),
            servicePriceList: Yup.object<{ [priceType in EPriceType]?: ServicePrice }>({
              GPRS: commsServicePriceSchema,
              GSM: commsServicePriceSchema,
              PSTN: commsServicePriceSchema,
              PAKNET: commsServicePriceSchema,
              CustomerOwned: commsServicePriceSchema
            })
          })
        ).optional()
      })
    ).test('at-least-one-not-deleted-service', 'At least one service is required', (services?: Service[]) => {
      if (!services) return false;
      const filtered = services.filter(service => !service.markedForDeletion);
      return filtered.length > 0;
    }),
    meterPurchasePrices: Yup.array<MeterPurchasePrice>(
      Yup.object({
        siteTypeId: Yup.string().required('Field is required'),
        annualBasePrice: requiredNumberFieldValidator
      })
    ).optional(),
    features: Yup.array<Feature>(
      Yup.object({
        featureTypeId: Yup.string().required('Field is required'),
        attributes: Yup.array(
          Yup.object({
            attributeName: Yup.string().required('Field is required')
          })
        ).optional()
      })
    ).optional(),
    invoices: invoiceValidationSchema,
    mpidList: isSupplier
      ? Yup.array(
          Yup.object<Mpid>({
            tpContactId: Yup.string().required('Field is required'),
            contact: Yup.object({
              industryMpid: Yup.string().required('Field is required'),
              roleCode: Yup.string().required('Field is required'),
              contactDescription: Yup.string().optional()
            })
          })
        )
          .required('At least one service is required')
          .min(1, 'At least one MPID is required')
      : Yup.array<Mpid>().optional(),
    premiumPrices: Yup.array(
      Yup.object<Premium>({
        siteTypeId: Yup.string().required('Field is required'),
        premiumJobId: Yup.string().required('Field is required'),
        annualBasePrice: requiredNumberFieldValidator
      })
    ).test('no-duplicate-premium-prices', 'Duplicate Premium Prices', (premiumPrices?: Premium[]) => {
      if (!premiumPrices) return true;
      const seen = new Set();
      for (const price of premiumPrices) {
        if (price.markedForDeletion) continue;
        const identifier = `${price.siteTypeId}-${price.premiumJobId}`;
        if (seen.has(identifier)) {
          return false;
        }
        seen.add(identifier);
      }
      return true;
    })
  });
};
